import {Row, Col, Card, Table, Button, Input, Tooltip, Image, Switch, Spin, Modal, Skeleton, Badge} from "antd";
import React, { useState, useEffect } from "react";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import useDebounce from "../../hooks/useDebounce";
import ShowTotal from "../../components/ShowTotal";
import ConfirmationBox from "../../components/ConfirmationBox";
import apiPath from "../../constants/apiPath";
import moment from 'moment';
import { formatDate } from "../../helper/DateHelper";
import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import From from "./From";
import { getSortOrder } from "../../constants/conts";
const Search = Input.Search;

function Index() {

  const sectionName   =   "City";
  const routeName     =   "city";

  const api = {
    status    :   apiPath.statusCity,
    addEdit   :   apiPath.addEditCity,
    list      :   apiPath.listCity,
    view      :   apiPath.viewCity
  }

  const [searchText, setSearchText]   =   useState('');
  const { request }                   =   useRequest()
  const [list, setList]               =   useState([])
  const { showConfirm }               =   ConfirmationBox()
  const [loading, setLoading]         =   useState(false)
  const [refresh, setRefresh]         =   useState(false)
  const [pagination, setPagination]   =   useState({ current: 1, pageSize: 20 });
  const [sortBy, setSortBy]           =   useState('');
  const [filters, setFilters]         =   useState({});
  const debouncedSearchText           =   useDebounce(searchText, 300);
  const [visible, setVisible]         =   useState(false);
  const [selected, setSelected]       =   useState();

  // View Modal States
  const [viewVisible, setViewVisible] = useState(false);
  const [viewData, setViewData]       = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "name"),
      render: (_, { name }) => {
        return (
          name ? <span className="cap">{name}</span> : '-'
        );
      },
    },
    {
      title: "Status",
      key: "is_active",
      filters: [
        {
          text: 'Active',
          value: true,
        },
        {
          text: 'Inactive',
          value: false,
        },
      ],
      render: (_, { is_active, _id }) => {
        return (
          <Switch
            checked = {is_active}
            checkedChildren = {<CheckOutlined />}
            unCheckedChildren = {<CloseOutlined />}
            loading = {loading}
            onChange = {(checked) =>
              showConfirm({
                record    : _id,
                path      : api.status,
                onLoading : () => setLoading(true),
                onSuccess : () => setRefresh((prev) => !prev),
                is_active : checked,
              })
            }
            className={is_active ? "custom-switch-checked" : ""}
          />
        );
      },
    },
    {
      title: "Created On",
      key: "created_at",
      dataIndex: "created_at",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "created_at"),
      render: (_, { created_at }) => {
        return (
          formatDate(created_at)
        );
      },
    },
    {
      title: "Action",
      fixed: 'right',
      render: (_, record) => {
        return (
          <>
            <Tooltip title={"Update " + sectionName} color={"purple"} key={"update" + routeName}>
              <Button onClick={() => {
                setSelected(record)
                setVisible(true)
              }}>
                <i className="fa fa-light fa-pen"></i>
              </Button>
            </Tooltip>

            <Tooltip title={"View " + sectionName} color={"purple"} key={"view"}>
              <Button onClick={() => handleView(record._id)}>
                <i className="fa fa-light fa-eye"></i>
              </Button>
            </Tooltip>
          </>
        );
      },
    },
  ];

  useEffect(() => {
    setLoading(true)
    fetchData(pagination, filters, sortBy)
  }, [refresh, debouncedSearchText, sortBy, pagination.current, pagination.pageSize, filters])

  const fetchData = (pagination, filters, sortValue = '') => {
    
    const filterActive = filters ? filters.is_active : null;

    request({
      url:
        api.list +
        `?status=${filterActive ? filterActive.join(',') : ''}` +
        `&page=${pagination.current}` +
        `&limit=${pagination.pageSize}` +
        `&search=${debouncedSearchText}` +
        `&sort=${sortValue || ''}`,
      method: 'GET',
      onSuccess: (data) => {
        setLoading(false);
        setList(data.data.list.docs);
        setPagination(prev => ({
          ...prev,
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: data.data.list.totalDocs,
        }));
      },
      onError: (error) => {
        console.log(error);
        ShowToast(error, Severty.ERROR);
      },
    });
  }

  const handleChange = (pag, newFilters, sorter) => {
    const { order, field } = sorter || {};

    let nextSort = '';
    if (order) {
      nextSort = `${field}:${order === 'ascend' ? 'asc' : 'desc'}`;
    }

    setFilters(newFilters || {});

    setSortBy(nextSort);

    setPagination(prev => ({
      ...prev,
      current: pag?.current || 1,
      pageSize: pag?.pageSize || prev.pageSize,
    }));
  };

  const onSearch = (e) => {
    setSearchText(e.target.value)
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleView = (id) => {
    setViewVisible(true);
    setViewLoading(true);
    setViewData(null);

    request({
      url: `${api.view}/${id}`,
      method: 'GET',
      onSuccess: (res) => {
        setViewData(res?.data || {});
        setViewLoading(false);
      },
      onError: (err) => {
        console.log(err);
        ShowToast("Failed to load city details", Severty.ERROR);
        setViewLoading(false);
      }
    })
  };

  return (
    <>
      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs="24" xl={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              title={sectionName + " Management"}
              extra={
                <>
                  <Search
                    size="large"
                    onChange={onSearch}
                    value={searchText}
                    onPressEnter={onSearch}
                    placeholder="Search By Name"
                  />
                  <Button onClick={(e) => { setVisible(true); setSearchText(''); }}>Add New {sectionName}</Button>
                </>
              }
            >
              <h4 className="text-right mb-1">{pagination.total ? ShowTotal(pagination.total) : ShowTotal(0)}</h4>
              <div className="table-responsive customPagination">
                <Table
                  loading={loading}
                  columns={columns}
                  dataSource={list}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    responsive: true,
                    defaultPageSize: 20,
                    pageSizeOptions: ['10', '20', '30', '50', '100']
                  }}
                  onChange={handleChange}
                  className="ant-border-space"
                />
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Add Modal */}
      {visible && 
        <From
          path={api.addEdit}
          sectionName={sectionName}
          show={visible}
          hide={() => { setSelected(); setVisible(false) }}
          data={selected}
          refresh={() => {
            setRefresh(prev => !prev)
          }}
        />
      }

      {/* View Modal */}
      <Modal
        title={`${sectionName} Details`}
        open={viewVisible}
        onCancel={() => setViewVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewVisible(false)}>
            Close
          </Button>
        ]}
        centered
        width={600}
      >
        {viewLoading ? (
          <Skeleton active paragraph={{ rows: 2 }} />

        ) : viewData ? (

          <div className="view-main-list p-5">
            <Row gutter={[16, 16]}>
              <Col span={24} xs={24} md={24}>
                <div className="view-inner-cls">
                  <h5>Name:</h5>
                  <h6>{viewData.name || "-"}</h6>
                </div>
              </Col>

              <Col span={24} xs={24} md={24}>
                <div className="view-inner-cls">
                  <h5>Status:</h5>
                  <h6>
                    {viewData.is_active ? (
                      <Badge status="success" text="Active" />
                    ) : (
                      <Badge status="error" text="Inactive" />
                    )}
                  </h6>
                </div>
              </Col>

              <Col span={24} xs={24} md={24}>
                <div className="view-inner-cls">
                  <h5>Created On:</h5>
                  <h6>
                    {formatDate(viewData.created_at)}
                  </h6>
                </div>
              </Col>
            </Row>
          </div>
        ) : (
          <p>No data available</p>
        )}
      </Modal>
    </>
  );
}

export default Index;
